import React, { useEffect, useState } from 'react';
import { RefreshCw, MessageSquare, Search, ChevronLeft, ChevronRight, User, Mail } from 'lucide-react';
import { superuserService } from '../../services/api';

interface ConversationUser {
  id: number;
  nom: string;
  prenom: string;
  role: string;
}

interface Conversation {
  user1: ConversationUser;
  user2: ConversationUser;
  last_message: {
    id: number;
    contenu: string;
    created_at: string;
    is_read: boolean;
  };
  apartment: {
    id: number;
    titre: string;
  } | null;
  message_count: number;
}

interface MessageDetail {
  id: number;
  sender_id: number;
  receiver_id: number;
  contenu: string;
  created_at: string;
  is_read: boolean;
  sender: { id: number; nom: string; prenom: string };
  receiver: { id: number; nom: string; prenom: string };
}

const AdminMessagesPage: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<{ user1: ConversationUser; user2: ConversationUser } | null>(null);
  const [messages, setMessages] = useState<MessageDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const response = await superuserService.getAllConversations();
      if (response.data?.conversations) {
        setConversations(response.data.conversations);
      }
    } catch (error: any) {
      console.error('Erreur chargement conversations:', error.response?.data || error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (user1Id: number, user2Id: number) => {
    setMessagesLoading(true);
    try {
      const response = await superuserService.getConversationDetail(user1Id, user2Id);
      if (response.data?.messages) {
        setMessages(response.data.messages);
      }
    } catch (error: any) {
      console.error('Erreur chargement messages:', error.response?.data || error);
    } finally {
      setMessagesLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, []);

  const handleSelectConversation = (conv: Conversation) => {
    setSelectedConversation({ user1: conv.user1, user2: conv.user2 });
    fetchMessages(conv.user1.id, conv.user2.id);
  };

  const getRoleBadge = (role: string) => {
    if (role === 'ROLE_GERANT') {
      return <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">Gérant</span>;
    }
    if (role === 'ROLE_LOCATAIRE') {
      return <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">Locataire</span>;
    }
    return <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">{role}</span>;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredConversations = conversations.filter(conv => {
    const search = searchTerm.toLowerCase();
    return (
      conv.user1.nom.toLowerCase().includes(search) ||
      conv.user1.prenom.toLowerCase().includes(search) ||
      conv.user2.nom.toLowerCase().includes(search) ||
      conv.user2.prenom.toLowerCase().includes(search) ||
      conv.last_message.contenu.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-heading font-bold text-neutral-900">Messages</h1>
          <p className="text-neutral-600">Tous les échanges entre locataires et gérants</p>
        </div>
        <button onClick={fetchConversations} className="btn btn-outline flex items-center gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Liste des conversations */}
        <div className="lg:col-span-1 space-y-4">
          <div className="card p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="card p-8 text-center text-neutral-500">
              Aucune conversation
            </div>
          ) : (
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {filteredConversations.map((conv, index) => (
                <div
                  key={index}
                  onClick={() => handleSelectConversation(conv)}
                  className={`card p-4 cursor-pointer hover:bg-gray-50 transition ${
                    selectedConversation?.user1.id === conv.user1.id && selectedConversation?.user2.id === conv.user2.id
                      ? 'ring-2 ring-primary-500 bg-primary-50'
                      : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-gray-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">{conv.user1.prenom} {conv.user1.nom}</span>
                        {getRoleBadge(conv.user1.role)}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                        <ChevronRight className="w-3 h-3" />
                        <span>{conv.user2.prenom} {conv.user2.nom}</span>
                        {getRoleBadge(conv.user2.role)}
                      </div>
                      <p className="text-sm text-gray-600 truncate mt-2">{conv.last_message.contenu}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-400">{formatDate(conv.last_message.created_at)}</span>
                        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full">{conv.message_count} msg</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Détail de la conversation */}
        <div className="lg:col-span-2">
          {selectedConversation ? (
            <div className="card h-[600px] flex flex-col">
              {/* Header */}
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-gray-500" />
                    <span className="font-medium">{selectedConversation.user1.prenom} {selectedConversation.user1.nom}</span>
                    {getRoleBadge(selectedConversation.user1.role)}
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{selectedConversation.user2.prenom} {selectedConversation.user2.nom}</span>
                    {getRoleBadge(selectedConversation.user2.role)}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedConversation(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messagesLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    Aucun message
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div key={msg.id} className="flex flex-col">
                      <div className={`max-w-[80%] ${msg.sender_id === selectedConversation.user1.id ? 'self-start' : 'self-end'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-gray-600">
                            {msg.sender.prenom} {msg.sender.nom}
                          </span>
                          <span className="text-xs text-gray-400">{formatDate(msg.created_at)}</span>
                        </div>
                        <div className={`p-3 rounded-lg ${
                          msg.sender_id === selectedConversation.user1.id
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-primary-500 text-white'
                        }`}>
                          <p className="text-sm whitespace-pre-wrap">{msg.contenu}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ) : (
            <div className="card h-[600px] flex items-center justify-center text-gray-500">
              <div className="text-center">
                <Mail className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>Sélectionnez une conversation pour voir les messages</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMessagesPage;
